import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getPrintableGroups } from "../lib/api";
import { printPage } from "../lib/print";

export default function PrintableGroupLists() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [groupType, setGroupType] = useState("morning");

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await getPrintableGroups(groupType);
      setGroups(res.data);
    } catch (err) {
      toast.error("Failed to load group lists");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [groupType]);

  const handlePrint = () => {
    if (!groups) return;

    const renderGroups = (groupData) => {
      if (!groupData) return "";
      const entries = Object.entries(groupData).filter(([, g]) => g.students.length > 0);
      if (entries.length === 0) return "<p>No groups with students</p>";

      let html = `<div style="display: flex; justify-content: center; gap: 0; flex-wrap: wrap;">`;
      entries.forEach(([key, group]) => {
        html += `<div style="flex: 1; min-width: 200px; max-width: 300px; text-align: center; padding: 16px 24px; border-right: 1px solid #ccc;">`;
        html += `<h3 style="font-size: 20px; font-weight: bold; text-decoration: underline; margin-bottom: 12px;">${group.name}</h3>`;
        if (group.image) {
          html += `<img src="${group.image}" style="max-width: 140px; max-height: 140px; margin: 0 auto 16px; display: block; object-fit: contain;" />`;
        }
        group.students.forEach((s) => {
          html += `<div style="font-size: 22px; line-height: 2.2;">${s.display_name}</div>`;
        });
        html += `</div>`;
      });
      html += `</div>`;
      return html;
    };

    let content = "";
    if (groups.morning) content += renderGroups(groups.morning);
    if (groups.afternoon) content += renderGroups(groups.afternoon);
    if (groups.advisory) content += renderGroups(groups.advisory);
    if (groups.grade) content += renderGroups(groups.grade);

    const titleMap = {
      morning: "Morning Groups",
      afternoon: "Afternoon Groups",
      advisory: "Advisory Groups",
      grade: "Grade Level Lists",
      all: "Group Lists",
    };

    printPage({
      title: titleMap[groupType] || "Group Lists",
      content,
      extraStyles: `
        @page { margin: 1cm; }
        img { print-color-adjust: exact !important; -webkit-print-color-adjust: exact !important; }
        div[style*="border-right"]:last-child { border-right: none !important; }
      `,
    });
  };

  const renderGroupColumn = (key, group) => (
    <div
      key={key}
      className="flex flex-col items-center text-center px-6 py-4 border-r last:border-r-0"
      data-testid={`group-column-${key}`}
    >
      <h3 className="text-lg font-bold underline mb-3">{group.name}</h3>
      {group.image ? (
        <img
          src={group.image}
          alt={group.name}
          className="max-w-[120px] max-h-[120px] object-contain mb-4"
          data-testid={`group-image-${key}`}
        />
      ) : (
        <div className="w-[120px] h-[120px] rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center mb-4 text-gray-400 text-xs">
          No image
        </div>
      )}
      <div className="space-y-1">
        {group.students.map((s) => (
          <p key={s.id} className="text-base leading-relaxed" data-testid={`student-name-${s.id}`}>
            {s.display_name}
          </p>
        ))}
        {group.students.length === 0 && (
          <p className="text-sm text-muted-foreground italic">No students</p>
        )}
      </div>
    </div>
  );

  const renderSection = (title, groupData) => {
    if (!groupData) return null;
    const entries = Object.entries(groupData).filter(([, g]) => g.students.length > 0);
    if (entries.length === 0) return null;

    return (
      <div className="space-y-3" data-testid={`section-${title.toLowerCase().replace(/\s/g, "-")}`}>
        <h2 className="text-lg font-semibold text-[#00305E]">{title}</h2>
        <div className="flex flex-wrap justify-center border rounded-lg bg-white overflow-hidden">
          {entries.map(([key, group]) => renderGroupColumn(key, group))}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="printable-group-lists">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle>Printable Group Lists</CardTitle>
            <CardDescription>Group names, images, and student first names for printing</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <Select value={groupType} onValueChange={setGroupType}>
              <SelectTrigger className="w-[180px]" data-testid="group-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="morning">Morning Groups</SelectItem>
                <SelectItem value="afternoon">Afternoon Groups</SelectItem>
                <SelectItem value="advisory">Advisory Groups</SelectItem>
                <SelectItem value="grade">Grade Level</SelectItem>
                <SelectItem value="all">All Groups</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={handlePrint}
              disabled={!groups || loading}
              className="bg-[#00305E] hover:bg-[#00305E]/90"
              data-testid="print-group-lists-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/>
              </svg>
              Print
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]"></div>
          </div>
        ) : groups ? (
          <div className="space-y-6">
            {renderSection("Morning Groups", groups.morning)}
            {renderSection("Afternoon Groups", groups.afternoon)}
            {renderSection("Advisory Groups", groups.advisory)}
            {renderSection("Grade Level", groups.grade)}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No group data available</p>
        )}
      </CardContent>
    </Card>
  );
}
